import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
} from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Case, CaseParty, PartyType, PartyRole, updateCaseParties } from '../../../../services/case';

interface CasePartiesProps {
  caseData: Case;
  canEdit: boolean;
  onRefresh: () => void;
}

const CaseParties: React.FC<CasePartiesProps> = ({ caseData, canEdit, onRefresh }) => {
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ourParties, setOurParties] = useState<CaseParty[]>([]);
  const [otherParties, setOtherParties] = useState<CaseParty[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingParty, setEditingParty] = useState<CaseParty | null>(null);
  const [partyType, setPartyType] = useState<PartyType>(PartyType.OUR_SIDE);
  const [form] = Form.useForm();

  // Get party role text
  const getPartyRoleText = (role: PartyRole): string => {
    const textMap: Record<PartyRole, string> = {
      [PartyRole.PLAINTIFF]: '原告',
      [PartyRole.DEFENDANT]: '被告',
      [PartyRole.CLIENT]: '委托人',
      [PartyRole.THIRD_PARTY]: '第三人',
      [PartyRole.ENFORCEMENT_APPLICANT]: '强制执行申请人',
      [PartyRole.ENFORCEMENT_RESPONDENT]: '被申请执行人',
      [PartyRole.SUSPECT_FAMILY]: '犯罪嫌疑人家属',
      [PartyRole.SUSPECT]: '犯罪嫌疑人',
    };
    return textMap[role] || role;
  };

  // Initialize parties when entering edit mode
  const handleEdit = () => {
    const our = (caseData.parties || []).filter(p => p.party_type === PartyType.OUR_SIDE);
    const other = (caseData.parties || []).filter(p => p.party_type === PartyType.OTHER_SIDE);
    setOurParties([...our]);
    setOtherParties([...other]);
    setEditMode(true);
  };

  // Handle cancel
  const handleCancel = () => {
    setOurParties([]);
    setOtherParties([]);
    setEditMode(false);
  };

  // Handle save
  const handleSave = async () => {
    try {
      setLoading(true);
      const allParties = [...ourParties, ...otherParties];
      await updateCaseParties(caseData.id, allParties);
      message.success('保存成功');
      setEditMode(false);
      onRefresh();
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle add party
  const handleAddParty = (type: PartyType) => {
    setPartyType(type);
    setEditingParty(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Handle edit party
  const handleEditParty = (party: CaseParty, type: PartyType) => {
    setPartyType(type);
    setEditingParty(party);
    form.setFieldsValue(party);
    setModalVisible(true);
  };

  // Handle delete party
  const handleDeleteParty = (party: CaseParty, type: PartyType) => {
    if (type === PartyType.OUR_SIDE) {
      setOurParties(ourParties.filter(p => p !== party));
    } else {
      setOtherParties(otherParties.filter(p => p !== party));
    }
  };

  // Handle modal OK
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const newParty: CaseParty = {
        ...values,
        party_type: partyType,
      };

      if (editingParty) {
        // Update existing party
        if (partyType === PartyType.OUR_SIDE) {
          setOurParties(ourParties.map(p => (p === editingParty ? newParty : p)));
        } else {
          setOtherParties(otherParties.map(p => (p === editingParty ? newParty : p)));
        }
      } else {
        // Add new party
        if (partyType === PartyType.OUR_SIDE) {
          setOurParties([...ourParties, newParty]);
        } else {
          setOtherParties([...otherParties, newParty]);
        }
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      // Validation failed
    }
  };

  // Table columns
  const columns: ColumnsType<CaseParty> = [
    {
      title: '当事人角色',
      dataIndex: 'party_role',
      key: 'party_role',
      render: (role: PartyRole) => getPartyRoleText(role),
    },
    {
      title: '姓名/名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      render: (text) => text || '-',
    },
    {
      title: '身份类型',
      dataIndex: 'identity_type',
      key: 'identity_type',
      render: (text) => text || '-',
    },
    {
      title: '身份证号/统一社会信用代码',
      dataIndex: 'identity_number',
      key: 'identity_number',
      render: (text) => text || '-',
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || '-',
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  // Edit mode columns
  const editColumns: ColumnsType<CaseParty> = [
    ...columns,
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditParty(record, record.party_type)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此当事人吗？"
            onConfirm={() => handleDeleteParty(record, record.party_type)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title="当事人信息"
        extra={
          canEdit && !editMode && (
            <Button type="link" icon={<EditOutlined />} onClick={handleEdit}>
              编辑
            </Button>
          )
        }
      >
        {editMode && (
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
              >
                保存
              </Button>
              <Button icon={<CloseOutlined />} onClick={handleCancel}>
                取消
              </Button>
            </Space>
          </div>
        )}

        {/* Our Side Parties */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0 }}>我方当事人</h4>
            {editMode && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleAddParty(PartyType.OUR_SIDE)}
              >
                添加我方当事人
              </Button>
            )}
          </div>
          <Table
            columns={editMode ? editColumns : columns}
            dataSource={editMode ? ourParties : (caseData.parties || []).filter(p => p.party_type === PartyType.OUR_SIDE)}
            rowKey={(record, index) => `our-${record.id || index}`}
            pagination={false}
            size="small"
            locale={{ emptyText: '暂无我方当事人' }}
          />
        </div>

        {/* Other Side Parties */}
        <div>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0 }}>对方当事人</h4>
            {editMode && (
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleAddParty(PartyType.OTHER_SIDE)}
              >
                添加对方当事人
              </Button>
            )}
          </div>
          <Table
            columns={editMode ? editColumns : columns}
            dataSource={editMode ? otherParties : (caseData.parties || []).filter(p => p.party_type === PartyType.OTHER_SIDE)}
            rowKey={(record, index) => `other-${record.id || index}`}
            pagination={false}
            size="small"
            locale={{ emptyText: '暂无对方当事人' }}
          />
        </div>
      </Card>

      {/* Add/Edit Party Modal */}
      <Modal
        title={editingParty ? '编辑当事人' : '添加当事人'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="当事人角色"
            name="party_role"
            rules={[{ required: true, message: '请选择当事人角色' }]}
          >
            <Select placeholder="请选择当事人角色">
              <Select.Option value={PartyRole.PLAINTIFF}>原告</Select.Option>
              <Select.Option value={PartyRole.DEFENDANT}>被告</Select.Option>
              <Select.Option value={PartyRole.CLIENT}>委托人</Select.Option>
              <Select.Option value={PartyRole.THIRD_PARTY}>第三人</Select.Option>
              <Select.Option value={PartyRole.ENFORCEMENT_APPLICANT}>强制执行申请人</Select.Option>
              <Select.Option value={PartyRole.ENFORCEMENT_RESPONDENT}>被申请执行人</Select.Option>
              <Select.Option value={PartyRole.SUSPECT_FAMILY}>犯罪嫌疑人家属</Select.Option>
              <Select.Option value={PartyRole.SUSPECT}>犯罪嫌疑人</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="姓名/名称"
            name="name"
            rules={[{ required: true, message: '请输入姓名/名称' }]}
          >
            <Input placeholder="请输入姓名/名称" />
          </Form.Item>

          <Form.Item label="性别" name="gender">
            <Select placeholder="请选择性别" allowClear>
              <Select.Option value="男">男</Select.Option>
              <Select.Option value="女">女</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="身份类型" name="identity_type">
            <Select placeholder="请选择身份类型" allowClear>
              <Select.Option value="身份证">身份证</Select.Option>
              <Select.Option value="统一社会信用代码">统一社会信用代码</Select.Option>
              <Select.Option value="护照">护照</Select.Option>
              <Select.Option value="其他">其他</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="身份证号/统一社会信用代码" name="identity_number">
            <Input placeholder="请输入身份证号/统一社会信用代码" />
          </Form.Item>

          <Form.Item label="联系电话" name="phone">
            <Input placeholder="请输入联系电话" />
          </Form.Item>

          <Form.Item label="地址" name="address">
            <Input.TextArea rows={2} placeholder="请输入地址" />
          </Form.Item>

          <Form.Item label="法定代表人" name="legal_representative">
            <Input placeholder="请输入法定代表人（企业）" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CaseParties;
